import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { motion } from "motion/react";
import { createPortal, flushSync } from "react-dom";
import { useReactToPrint } from "react-to-print";
import { getCmsMenus, cmsUpdateItem, cmsDeleteItem, cmsCreateSection, getCmsLayout, getCmsAssets } from "api";
import SectionCard from "./SectionCard";
import ItemEditForm, { ModalPortal } from "./ItemEditForm";
import MenuPrint from "./MenuPrint";
import MenuStudio from "../MenuStudio/MenuStudio";
import { importPrintedMenu } from "../MenuStudio/importPrintedMenu";
import { resolveLayout } from "./layout/defaultLayout";
import "./MenuCMS.css";
import BackContext from "context/BackContext";
import { titleCase } from "./textUtils";
import Swal from "sweetalert2";
import { toast } from "sonner";

const MENU_TABS = [
  { key: "dinner", label: "Dinner" },
  { key: "lunch", label: "Lunch" },
  { key: "wine", label: "Beverages" },
];

const RECENT_OPTION = {
  key: "recent",
  label: "Recent Changes",
  detail: "Only pages with edits from the last 24 hours",
};

const EXPORT_OPTIONS = [
  {
    key: "all",
    label: "All",
    detail: "Main menu, lunch specials & beverage list",
  },
  { key: "dinner", label: "Main Menu" },
  { key: "lunch", label: "Lunch Specials" },
  { key: "wine", label: "Beverage List" },
];

function exportTitle(selection) {
  if (selection.includes("recent"))
    return "Trattoria Demi — Recent Menu Changes";
  if (selection.includes("all")) return "Trattoria Demi — Menus";
  if (selection.length === 1) {
    const one = EXPORT_OPTIONS.find((o) => o.key === selection[0]);
    return `Trattoria Demi — ${one ? one.label : "Menu"}`;
  }
  return "Trattoria Demi — Menus";
}

function MenuCMS({ startInStudio = false, initialStudioContext = "full", onStudioContextChange, onExitStudio }) {
  const [menus, setMenus] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [activeTab, setActiveTab] = useState("dinner");
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportSel, setExportSel] = useState(["all"]);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [newSecOpen, setNewSecOpen] = useState(false);
  const [newSecHeader, setNewSecHeader] = useState("");
  const [newSecDesc, setNewSecDesc] = useState("");
  const [newSecSaving, setNewSecSaving] = useState(false);
  // Print design (Menu Designer): saved layout document + uploaded artwork.
  const [layoutDoc, setLayoutDoc] = useState(null);
  const [assets, setAssets] = useState([]);
  const [designerOpen, setDesignerOpen] = useState(false);
  const [studioSeeds, setStudioSeeds] = useState(null);
  const [studioLoading, setStudioLoading] = useState(false);
  const [studioStartupError, setStudioStartupError] = useState("");
  const [studioExport, setStudioExport] = useState(false);
  const studioOpeningRef = useRef(false);
  const studioAutoOpenedRef = useRef(false);

  async function openStudio(exportImmediately = false, initialMenus = null) {
    if (studioOpeningRef.current) return;
    studioOpeningRef.current = true;
    setStudioLoading(true);
    setStudioStartupError("");
    try {
      // A saved design may link to items added in another session. Refresh before
      // mounting Studio so reconciliation cannot delete those links as stale.
      // The dashboard entry already has a fresh CMS snapshot from loadMenus.
      const freshMenus = initialMenus || await getCmsMenus();
      if (!initialMenus) flushSync(() => setMenus(freshMenus));
      const seeds = await importPrintedMenu(printRef.current, freshMenus);
      setStudioSeeds(seeds);
      setStudioExport(exportImmediately);
      studioAutoOpenedRef.current = true;
      setDesignerOpen(true);
      if (!startInStudio) onStudioContextChange?.("full");
    } catch (error) {
      const message = error.message || "Could not open the original PDF design.";
      setStudioStartupError(message);
      toast.error(message);
    }
    finally { studioOpeningRef.current = false; setStudioLoading(false); }
  }
  const { setter } = useContext(BackContext);
  const studioBridge = useRef(null);
  const printRef = useRef(null);
  const sectionRefs = useRef({});
  const sidebarRef = useRef(null);
  const topbarRef = useRef(null);
  const anchorOffsetRef = useRef(110);
  const spyLock = useRef(false);
  const spyTimer = useRef(null);
  const cmsScroller = () => designerOpen
    ? document.querySelector(".ms-cms-workspace") || window
    : window;

  const exportPdf = useReactToPrint({
    content: () => printRef.current,
    documentTitle: exportTitle(exportSel),
  });

  useEffect(() => {
    setter([{ body: "body", tag: "" }]);
  }, [setter]);

  useEffect(() => {
    loadMenus();
    // Initial CMS data is fetched once; later updates use the mutation helpers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!startInStudio || !menus || studioAutoOpenedRef.current) return;
    studioAutoOpenedRef.current = true;
    openStudio(false, menus);
    // Opening is tied to the first loaded CMS snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startInStudio, menus]);

  const sectionsByTab = useMemo(() => {
    const isBeer = (section) => String(section.header || "").trim().toUpperCase() === "BEER";
    return {
      dinner: (menus?.dinner?.sections || []).filter((section) => !isBeer(section)),
      lunch: menus?.lunch?.sections || [],
      wine: [...(menus?.dinner?.sections || []).filter(isBeer), ...(menus?.wine?.sections || [])],
    };
  }, [menus]);
  const sections = sectionsByTab[activeTab];
  const editableItems = useMemo(() => MENU_TABS.flatMap(({ key, label }) =>
    sectionsByTab[key].flatMap((section) => (section.items || []).map((item) =>
      ({ item, section, tab: key, label })))
  ), [sectionsByTab]);
  const editingIndex = editableItems.findIndex(({ item }) => item._id === editingItemId);
  const editingEntry = editableItems[editingIndex];

  function openItem(itemId) {
    const entry = editableItems.find(({ item }) => item._id === itemId);
    if (!entry) return;
    setEditingItemId(itemId);
    setActiveTab(entry.tab);
    setSelectedSectionId(entry.section._id);
  }

  async function saveEditedItem(payload, direction) {
    const item = editingEntry.item;
    await cmsUpdateItem(item._id, payload);
    updateItemInState({ ...item, ...payload, updatedAt: new Date().toISOString() });
    if (direction) openItem(editableItems[editingIndex + direction]?.item._id);
    else setEditingItemId(null);
  }

  // Deleting also removes the item from the menu designs (server side); an
  // open Studio saves first and then shows the updated design.
  async function deleteEditedItem() {
    await studioBridge.current?.beforeCmsDelete?.();
    await cmsDeleteItem(editingEntry.item._id);
    deleteItemFromState(editingEntry.item._id);
    setEditingItemId(null);
    await studioBridge.current?.afterCmsDelete?.();
  }

  // ─── Scroll spy: mark the section currently in view as active ────────────

  useEffect(() => {
    function onScroll() {
      if (spyLock.current || !sections?.length) return;
      const scroller = cmsScroller();
      const anchor = scroller === window ? 140 : scroller.getBoundingClientRect().top + 24;
      let best = null;
      let bestTop = -Infinity;
      for (const s of sections) {
        const el = sectionRefs.current[s._id];
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= anchor && top > bestTop) {
          bestTop = top;
          best = s._id;
        }
      }
      setSelectedSectionId(best || sections[0]._id);
    }
    const scroller = cmsScroller();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => scroller.removeEventListener("scroll", onScroll);
    // The scroll container changes when the CMS moves into Studio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, designerOpen]);

  useEffect(() => () => clearTimeout(spyTimer.current), []);

  // Tail spacer: exactly enough room for the last section to scroll up to
  // the anchor line — no more, so the page can't over-scroll into blank.
  useEffect(() => {
    const rootEl = topbarRef.current?.parentElement;
    if (!rootEl) return;
    const lastId = sections?.length
      ? sections[sections.length - 1]._id
      : null;
    const last = lastId ? sectionRefs.current[lastId] : null;
    if (!last) {
      rootEl.style.setProperty("--mx-tail", "0px");
      return;
    }
    const update = () => {
      // 110 = the page's own bottom padding, already part of the runway
      const tail = Math.max(
        0,
        window.innerHeight - anchorOffsetRef.current - last.offsetHeight - 110
      );
      rootEl.style.setProperty("--mx-tail", `${tail}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(last);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [sections, loading]);

  // Stick the topbar and sidebar at the exact offsets they render at.
  useEffect(() => {
    const bar = topbarRef.current;
    if (bar) {
      const top = Math.round(bar.getBoundingClientRect().top + window.scrollY);
      bar.style.top = `${top}px`;
      bar.style.setProperty("--mx-topbar-gap", `${top}px`);
      // Click-to-scroll must land sections below the stuck bar.
      bar.parentElement?.style.setProperty(
        "--mx-anchor-offset",
        `${top + bar.offsetHeight + 17}px`
      );
    }
    const el = sidebarRef.current;
    if (!el) return;
    const top = Math.round(el.getBoundingClientRect().top + window.scrollY);
    el.style.top = `${top}px`;
    el.style.maxHeight = `calc(100vh - ${top + 16}px)`;
    // Clicked sections align with the sidebar's stuck position.
    anchorOffsetRef.current = top;
  }, [loading]);

  function handleSelectSection(id) {
    const el = sectionRefs.current[id];
    if (!el) return;
    setSelectedSectionId(id);
    spyLock.current = true;
    // offsetTop chain ignores CSS transforms, so an in-flight entrance
    // animation can't skew the target position.
    let target = 0;
    for (let node = el; node; node = node.offsetParent) {
      target += node.offsetTop;
    }
    const scroller = cmsScroller();
    if (scroller === window) {
      window.scrollTo({ top: target - anchorOffsetRef.current, behavior: "smooth" });
    } else {
      scroller.scrollTo({
        top: scroller.scrollTop + el.getBoundingClientRect().top - scroller.getBoundingClientRect().top - 16,
        behavior: "smooth",
      });
    }
    clearTimeout(spyTimer.current);
    spyTimer.current = setTimeout(() => {
      spyLock.current = false;
    }, 900);
  }

  async function loadMenus() {
    try {
      setLoading(true);
      setStudioStartupError("");
      const [data, layoutRes, assetRes] = await Promise.all([
        getCmsMenus(),
        getCmsLayout().catch(() => ({ doc: null })),
        getCmsAssets().catch(() => []),
      ]);
      setMenus(data);
      setLayoutDoc(layoutRes?.doc ?? null);
      setAssets(Array.isArray(assetRes) ? assetRes : []);
    } catch (err) {
      console.error(err);
      setStudioStartupError("Menu data is unavailable. Try again.");
      if (!startInStudio) Swal.fire({
        title: "Error",
        text: "Failed to load menu data.",
        icon: "error",
        background: "#151319",
        color: "#f8f4f1",
        confirmButtonColor: "#d3963a",
      });
    } finally {
      setLoading(false);
    }
  }

  // ─── State mutation helpers ───────────────────────────────────────────────

  function updateItemInState(updatedItem) {
    setMenus((prev) => {
      const next = {};
      for (const [type, menu] of Object.entries(prev)) {
        next[type] = {
          ...menu,
          sections: menu.sections.map((section) => ({
            ...section,
            items: section.items.map((item) =>
              item._id === updatedItem._id ? updatedItem : item
            ),
          })),
        };
      }
      return next;
    });
  }

  function deleteItemFromState(itemId) {
    setMenus((prev) => {
      const next = {};
      for (const [type, menu] of Object.entries(prev)) {
        next[type] = {
          ...menu,
          sections: menu.sections.map((section) => ({
            ...section,
            items: section.items.filter((item) => item._id !== itemId),
          })),
        };
      }
      return next;
    });
  }

  function addItemToState(newItem) {
    setMenus((prev) => {
      const next = {};
      for (const [type, menu] of Object.entries(prev)) {
        next[type] = {
          ...menu,
          sections: menu.sections.map((section) =>
            section._id === newItem.sectionId
              ? { ...section, items: [...section.items, newItem] }
              : section
          ),
        };
      }
      return next;
    });
  }

  function updateSectionInState(updatedSection) {
    setMenus((prev) => {
      const next = {};
      for (const [type, menu] of Object.entries(prev)) {
        next[type] = {
          ...menu,
          sections: menu.sections.map((section) =>
            section._id === updatedSection._id
              ? { ...section, ...updatedSection }
              : section
          ),
        };
      }
      return next;
    });
  }

  function deleteSectionFromState(sectionId) {
    setMenus((prev) => {
      const next = {};
      for (const [type, menu] of Object.entries(prev)) {
        next[type] = {
          ...menu,
          sections: menu.sections.filter((s) => s._id !== sectionId),
        };
      }
      return next;
    });
  }

  function addSectionToState(newSection) {
    setMenus((prev) => {
      const next = {};
      for (const [type, menu] of Object.entries(prev)) {
        if (String(menu._id) === String(newSection.menuId)) {
          next[type] = {
            ...menu,
            sections: [
              ...menu.sections,
              { ...newSection, items: newSection.items || [] },
            ],
          };
        } else {
          next[type] = menu;
        }
      }
      return next;
    });
  }

  function reorderItemsInState(sectionId, itemIds) {
    const orderIndex = new Map(itemIds.map((id, i) => [id, i]));
    setMenus((prev) => {
      const next = {};
      for (const [type, menu] of Object.entries(prev)) {
        next[type] = {
          ...menu,
          sections: menu.sections.map((section) =>
            section._id === sectionId
              ? {
                  ...section,
                  items: [...section.items]
                    .sort(
                      (a, b) =>
                        (orderIndex.get(a._id) ?? Infinity) -
                        (orderIndex.get(b._id) ?? Infinity)
                    )
                    .map((item) =>
                      orderIndex.has(item._id)
                        ? { ...item, sortOrder: orderIndex.get(item._id) }
                        : item
                    ),
                }
              : section
          ),
        };
      }
      return next;
    });
  }

  const handlers = {
    beforeCmsDelete: () => studioBridge.current?.beforeCmsDelete?.(),
    afterCmsDelete: () => studioBridge.current?.afterCmsDelete?.(),
    updateItem: updateItemInState,
    reorderItems: reorderItemsInState,
    deleteItem: deleteItemFromState,
    addItem: addItemToState,
    updateSection: updateSectionInState,
    deleteSection: deleteSectionFromState,
    addSection: addSectionToState,
  };

  // ─── Existing modifiers for quick-add chips ───────────────────────────────

  const existingModifiers = useMemo(() => {
    if (!menus)
      return { sizes: [], options: [], sauces: [], pastas: [], platters: [] };
    const maps = {
      sizes: new Map(),
      options: new Map(),
      sauces: new Map(),
      pastas: new Map(),
      platters: new Map(),
    };
    for (const menu of Object.values(menus)) {
      if (!menu?.sections) continue;
      for (const section of menu.sections) {
        for (const item of section.items) {
          for (const key of Object.keys(maps)) {
            for (const entry of item[key] || []) {
              if (entry.name?.trim() && !maps[key].has(entry.name.trim())) {
                maps[key].set(entry.name.trim(), entry.price ?? 0);
              }
            }
          }
        }
      }
    }
    return Object.fromEntries(
      Object.entries(maps).map(([key, map]) => [
        key,
        Array.from(map.entries()).map(([name, price]) => ({ name, price })),
      ])
    );
  }, [menus]);

  // ─── Item counts per menu (for tab badges) ────────────────────────────────

  const itemCounts = useMemo(() => {
    const counts = {};
    if (!menus) return counts;
    for (const [type, menu] of Object.entries(menus)) {
      counts[type] = (menu?.sections || []).reduce(
        (sum, s) => sum + (s.items?.length || 0),
        0
      );
    }
    const beerItems = (menus.dinner?.sections || [])
      .filter((s) => String(s.header || "").trim().toUpperCase() === "BEER")
      .reduce((sum, s) => sum + (s.items?.length || 0), 0);
    counts.dinner = (counts.dinner || 0) - beerItems;
    counts.wine = (counts.wine || 0) + beerItems;
    return counts;
  }, [menus]);

  // ─── Add section ──────────────────────────────────────────────────────────

  function handleAddSection() {
    if (!menus?.[activeTab]?._id) return;
    setNewSecHeader("");
    setNewSecDesc("");
    setNewSecOpen(true);
  }

  async function handleCreateSection() {
    const menuId = menus?.[activeTab]?._id;
    const header = newSecHeader.trim();
    if (!menuId || !header || newSecSaving) return;
    setNewSecSaving(true);
    try {
      const newSection = await cmsCreateSection({
        header,
        description: newSecDesc.trim(),
        menuId,
      });
      addSectionToState(newSection);
      setNewSecOpen(false);
      setTimeout(() => handleSelectSection(newSection._id), 120);
    } catch (err) {
      console.error(err);
      toast.error("Failed to create section.");
    } finally {
      setNewSecSaving(false);
    }
  }

  // ─── Export ───────────────────────────────────────────────────────────────

  function toggleExportKey(key) {
    setExportSel((prev) => {
      if (key === "recent") return prev.includes("recent") ? [] : ["recent"];
      if (key === "all") return prev.includes("all") ? [] : ["all"];
      const next = prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [
            ...prev.filter((k) => k !== "recent" && k !== "all"),
            key,
          ];
      return next;
    });
  }

  // Anything touched in the last 24 hours? (drives the Recent Changes option)
  const hasRecentChanges = useMemo(() => {
    if (!menus) return false;
    const since = Date.now() - 24 * 60 * 60 * 1000;
    const fresh = (d) => d && new Date(d).getTime() > since;
    return Object.values(menus).some((menu) =>
      (menu?.sections || []).some(
        (s) => fresh(s.updatedAt) || (s.items || []).some((i) => fresh(i.updatedAt))
      )
    );
  }, [menus]);


  function handleExport() {
    setExportOpen(false);
    exportPdf();
  }

  const printLayout = useMemo(
    () => (menus ? resolveLayout(layoutDoc, menus) : null),
    [layoutDoc, menus]
  );

  const sectionList = sections || [];
  const activeSectionId =
    sectionList.find((s) => s._id === selectedSectionId)?._id ||
    sectionList[0]?._id ||
    null;

  const cmsView = (
    <div className="menu-cms">
      <div className="menu-cms-topbar" ref={topbarRef}>
        <div className="menu-cms-heading">
          <h2>Menu CMS</h2>
        </div>
        <div className="menu-cms-topbar-actions">
          <button
            className="submit-button new-payroll menu-cms-add-btn menu-cms-design-btn"
            onClick={() => openStudio(false)}
            disabled={!menus || studioLoading}
            title="Edit the original printed menu"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 20l4-1 11-11-3-3L5 16z" />
              <path d="M14 7l3 3" />
            </svg>
            {studioLoading ? "Opening Menu Studio…" : "Menu Studio"}
          </button>
          <button
            className="submit-button new-payroll menu-cms-add-btn menu-cms-export-btn"
            onClick={() => openStudio(true)}
            disabled={!menus || studioLoading}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 3v12" />
              <path d="M7 10l5 5 5-5" />
              <path d="M4 20h16" />
            </svg>
            Download Menu
          </button>
        </div>
      </div>

      {loading ? (
        <div className="menu-cms-state">
          <p>Loading menu data…</p>
        </div>
      ) : !menus ? (
        <div className="menu-cms-state">
          <p>Menu data is unavailable. Reload the page to try again.</p>
        </div>
      ) : (
        <div className="menu-cms-layout">
          <aside className="menu-cms-sidebar" ref={sidebarRef}>
            <div className="menu-cms-menuswitch">
              {MENU_TABS.map(({ key, label }) => (
                <button
                  key={key}
                  className={`menu-cms-menu-btn${
                    activeTab === key ? " menu-cms-menu-btn-active" : ""
                  }`}
                  onClick={() => {
                    setActiveTab(key);
                    setSelectedSectionId(null);
                    cmsScroller().scrollTo({ top: 0 });
                  }}
                >
                  {label}
                  <span className="menu-cms-menu-count">
                    {itemCounts[key] ?? 0}
                  </span>
                </button>
              ))}
            </div>

            <div className="menu-cms-secpanel">
              <nav className="menu-cms-seclist" aria-label="Menu sections">
              {sectionList.length === 0 && (
                <p className="menu-cms-seclist-empty">No sections yet.</p>
              )}
              {sectionList.map((section) => (
                <button
                  key={section._id}
                  className={`menu-cms-secnav${
                    activeSectionId === section._id
                      ? " menu-cms-secnav-active"
                      : ""
                  }`}
                  onClick={() => handleSelectSection(section._id)}
                >
                  <span className="menu-cms-secnav-name">
                    {titleCase(section.header)}
                  </span>
                  <span className="menu-cms-secnav-count">
                    {section.items.length}
                  </span>
                </button>
              ))}
              </nav>

              <button className="menu-cms-newsec" onClick={handleAddSection}>
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
                New section
              </button>
            </div>
          </aside>

          <main className="menu-cms-content">
            {sectionList.length === 0 ? (
              <div className="menu-cms-state">
                <p>Create a section to start building this menu.</p>
              </div>
            ) : (
              sectionList.map((section) => (
                <motion.div
                  key={section._id}
                  className="menu-cms-secanchor"
                  ref={(el) => {
                    sectionRefs.current[section._id] = el;
                  }}
                  initial={{ opacity: 0, y: 12, scale: 0.97 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ margin: "-90px 0px -8% 0px" }}
                  transition={{
                    duration: 0.3,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  <SectionCard
                    section={section}
                    handlers={handlers}
                    existingModifiers={existingModifiers}
                    onEditItem={openItem}
                  />
                </motion.div>
              ))
            )}
          </main>
        </div>
      )}

      {editingEntry && (
        <ItemEditForm
          key={editingEntry.item._id}
          item={editingEntry.item}
          existingModifiers={existingModifiers}
          onSave={saveEditedItem}
          onCancel={() => setEditingItemId(null)}
          onDelete={deleteEditedItem}
          navigation={{
            context: `${editingEntry.label} · ${titleCase(editingEntry.section.header)}`,
            position: editingIndex + 1,
            total: editableItems.length,
            hasPrev: editingIndex > 0,
            hasNext: editingIndex < editableItems.length - 1,
            onMove: (direction) => openItem(editableItems[editingIndex + direction]?.item._id),
          }}
        />
      )}

      {newSecOpen && (
        <ModalPortal>
        <div
          className="cms-export-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setNewSecOpen(false);
          }}
        >
          <div
            className="cms-export-modal cms-section-modal"
            role="dialog"
            aria-modal="true"
            aria-label="New section"
          >
            <header className="cms-export-head">
              <h3>New Section</h3>
              <p>
                Add a section to the{" "}
                {MENU_TABS.find((t) => t.key === activeTab)?.label.toLowerCase()}{" "}
                menu.
              </p>
            </header>
            <div className="cms-modal-body">
              <div className="cms-form-group">
                <label className="cms-form-label">Section Name</label>
                <input
                  className="cms-input"
                  value={newSecHeader}
                  onChange={(e) => setNewSecHeader(e.target.value)}
                  placeholder="e.g. Pasta"
                  autoFocus
                />
              </div>
              <div className="cms-form-group">
                <label className="cms-form-label">Description</label>
                <input
                  className="cms-input"
                  value={newSecDesc}
                  onChange={(e) => setNewSecDesc(e.target.value)}
                  placeholder="Optional description…"
                />
              </div>
            </div>
            <footer className="cms-export-foot">
              <span />
              <div className="cms-export-foot-actions">
                <button
                  className="cms-btn-cancel"
                  onClick={() => setNewSecOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="cms-btn-save cms-export-go"
                  onClick={handleCreateSection}
                  disabled={!newSecHeader.trim() || newSecSaving}
                >
                  {newSecSaving ? "Creating…" : "Create Section"}
                </button>
              </div>
            </footer>
          </div>
        </div>
        </ModalPortal>
      )}

      {exportOpen && (
        <ModalPortal>
        <div
          className="cms-export-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setExportOpen(false);
          }}
        >
          <div
            className="cms-export-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Export PDF"
          >
            <header className="cms-export-head">
              <h3>Download Menu</h3>
              <p>
                Choose which menus to download. In the print dialog, choose
                {" “Save as PDF”."}
              </p>
            </header>

            <div className="cms-export-options">
              {(() => {
                const opt = RECENT_OPTION;
                const checked = exportSel.includes(opt.key);
                return (
                  <button
                    type="button"
                    className={`cms-export-option${
                      checked ? " cms-export-option-checked" : ""
                    }`}
                    onClick={() => toggleExportKey(opt.key)}
                    disabled={!hasRecentChanges}
                    aria-pressed={checked}
                  >
                    <span className="cms-export-check">
                      {checked && (
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M4 12l5 5L20 6" />
                        </svg>
                      )}
                    </span>
                    <span className="cms-export-option-text">
                      <span className="cms-export-option-label">
                        {opt.label}
                      </span>
                      <span className="cms-export-option-detail">
                        {hasRecentChanges
                          ? opt.detail
                          : "No changes in the last 24 hours"}
                      </span>
                    </span>
                  </button>
                );
              })()}
              <div className="cms-export-divider" />
              {EXPORT_OPTIONS.map((opt) => {
                const checked = exportSel.includes(opt.key);
                return (
                  <button
                    key={opt.key}
                    type="button"
                    className={`cms-export-option${
                      checked ? " cms-export-option-checked" : ""
                    }`}
                    onClick={() => toggleExportKey(opt.key)}
                    aria-pressed={checked}
                  >
                    <span className="cms-export-check">
                      {checked && (
                        <svg
                          width="11"
                          height="11"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M4 12l5 5L20 6" />
                        </svg>
                      )}
                    </span>
                    <span className="cms-export-option-text">
                      <span className="cms-export-option-label">
                        {opt.label}
                      </span>
                      {opt.detail && (
                        <span className="cms-export-option-detail">
                          {opt.detail}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            <footer className="cms-export-foot">
              <span />
              <div className="cms-export-foot-actions">
                <button
                  className="cms-btn-cancel"
                  onClick={() => setExportOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className="cms-btn-save cms-export-go"
                  onClick={handleExport}
                  disabled={exportSel.length === 0}
                >
                  Save
                </button>
              </div>
            </footer>
          </div>
        </div>
        </ModalPortal>
      )}

    </div>
  );
  return (
    <>
      {designerOpen && menus ? (
        <MenuStudio
          menus={menus}
          cmsView={cmsView}
          onCmsUpdate={(type, updated) => type === "item" ? updateItemInState(updated) : updateSectionInState(updated)}
          initialDocuments={studioSeeds}
          initialExport={studioExport}
          importOriginal={() => importPrintedMenu(printRef.current, menus)}
          initialMenu={initialStudioContext === "takeout" ? "takeout" : "dinner"}
          initialView={initialStudioContext === "cms" ? "cms" : "design"}
          onContextChange={onStudioContextChange}
          bridgeRef={studioBridge}
          onClose={() => {
            setDesignerOpen(false);
            onExitStudio?.();
          }}
        />
      ) : startInStudio ? createPortal(
        <div className="menu-studio ms-startup-shell" role="status" aria-live="polite">
          <header className="ms-topbar">
            <div className="ms-brand"><strong>Menu Studio</strong></div>
            <div className="ms-document-title"><span>Opening your menu…</span></div>
          </header>
          <div className="ms-toolbar">
            <nav className="ms-context-switch" aria-label="Menu Studio views">
              <button type="button" className={initialStudioContext === "full" ? "is-active" : ""} aria-current={initialStudioContext === "full" ? "page" : undefined} disabled>Full menu</button>
              <button type="button" className={initialStudioContext === "takeout" ? "is-active" : ""} aria-current={initialStudioContext === "takeout" ? "page" : undefined} disabled>Takeout menu</button>
              <button type="button" className={initialStudioContext === "cms" ? "is-active" : ""} aria-current={initialStudioContext === "cms" ? "page" : undefined} disabled>CMS</button>
            </nav>
          </div>
          <div className="ms-loading">
            {!studioStartupError && <span className="ms-loading-indicator" aria-hidden="true" />}
            <h2>{studioStartupError ? "Could not open Menu Studio" : "Opening Menu Studio"}</h2>
            <p>{studioStartupError || "Loading your menu…"}</p>
            {studioStartupError && <button type="button" className="ms-startup-retry" onClick={() => menus ? openStudio(false, menus) : loadMenus()}>Try again</button>}
          </div>
        </div>, document.body
      ) : cmsView}
      <div className="menu-print-host" aria-hidden="true">
        <MenuPrint
          ref={printRef}
          menus={menus}
          layout={printLayout}
          assets={assets}
          selection={exportSel}
        />
      </div>
    </>
  );
}

export default MenuCMS;
